import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, MailOpen } from "lucide-react";
import { HERO_IMAGE, TRADES } from "../lib/content";

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden flex flex-col"
    >
      <img
        src={HERO_IMAGE}
        alt="A roofing contractor in a hi-vis vest and hard hat reading the LeadRiver dashboard on his iPhone while his crew installs new shingles behind him."
        className="absolute inset-0 w-full h-full object-cover"
        loading="eager"
      />
      <div className="absolute inset-0 hero-vignette" />
      <div className="grain" />

      <div className="relative z-10 flex-1 flex items-center">
        <div className="max-w-7xl mx-auto w-full px-6 pt-28 pb-20 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6">
            <motion.p
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="kicker"
              style={{ color: "#E07B3A" }}
            >
              LeadRiver · For trade contractors
            </motion.p>

            <motion.h1
              initial="hidden"
              animate="visible"
              variants={{
                hidden: {},
                visible: { transition: { staggerChildren: 0.12, delayChildren: 0.2 } },
              }}
              className="h-display text-white mt-6 text-[clamp(2.4rem,5.6vw,4.6rem)]"
              data-testid="hero-h1"
            >
              {["Never miss", "another lead."].map((line) => (
                <motion.span
                  key={line}
                  variants={{
                    hidden: { opacity: 0, y: 14 },
                    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
                  }}
                  className="block"
                >
                  {line}
                </motion.span>
              ))}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.7 }}
              className="font-serif-body text-white mt-7 max-w-xl text-[clamp(1.02rem,1.35vw,1.2rem)] leading-relaxed drop-shadow-[0_1px_8px_rgba(0,0,0,0.35)]"
              data-testid="hero-subhead"
            >
              LeadRiver watches your software and drafts your follow-ups while
              you're on a roof — or mowing a lawn, shotcreting a pool, spraying
              for bugs, or wiring a panel. You hit send. We never touch your
              customers.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.95, duration: 0.6 }}
              className="mt-10 flex flex-col items-start gap-3 max-w-md"
            >
              <a
                href="#founding"
                className="btn btn-primary w-full sm:w-auto"
                data-testid="hero-primary-cta"
              >
                Claim founding spot — 50% off year one
                <ArrowRight size={18} />
              </a>
              <a
                href="#brief"
                className="btn bg-white text-[#0B3D5C] hover:bg-[#FAF7F2] border border-white w-full sm:w-auto"
                data-testid="hero-secondary-cta"
              >
                <MailOpen size={18} /> See the 6am Morning Brief
              </a>
            </motion.div>
          </div>
        </div>
      </div>

      <div className="relative z-10">
        <div className="max-w-7xl mx-auto px-6 pb-10">
          <div className="border-t border-white/20 pt-6 flex flex-wrap gap-x-10 gap-y-3 items-center">
            <p className="kicker text-white/85">Built for</p>
            {TRADES.map((t, i) => (
              <span
                key={t.slug}
                className="font-sans-tight font-bold text-white text-[18px] tracking-tight"
              >
                {t.label}
                {i < TRADES.length - 1 && (
                  <span className="text-white/40 ml-10">·</span>
                )}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
